var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-1449 — the one control that adds a subfield to a field instance: a quiet
 * last row of the field ("+ Přidat podpole…") that turns into a combobox when it
 * gets the focus, by a click or by Tab.
 *
 * <p>It offers the optional subfields of the template first and, under a
 * separator, the others the MARC catalog allows (`utils/subfieldSuggestions.ts`);
 * typing filters by code or name, the arrows walk the list, Enter adds the
 * active one and Escape closes. The ARIA combobox pattern (an input owning a
 * listbox through `aria-activedescendant`), so the focus never leaves the input.
 *
 * <p>The same control, opened in place after a row (`placeholder` naming that
 * row, `autoFocus`), is "Vložit podpole za…": it is dismissed by Escape or by
 * leaving it, and `onDismiss` hands the focus back.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { preferredSuggestion, subfieldSuggestions } from '../utils/subfieldSuggestions';
import { MONO_FONT, subfieldCodeStyle } from '../fields/subfieldStyles';
var wrapStyle = {
    position: 'relative',
    marginBottom: '8px',
};
/** Quiet: no frame and no fill, so it never reads as an input left empty. */
var quietInputStyle = {
    width: '216px',
    maxWidth: '100%',
    padding: '4px 0',
    border: '1px solid transparent',
    background: 'transparent',
    fontSize: '13px',
    color: '#333',
    cursor: 'pointer',
};
var openInputStyle = __assign(__assign({}, quietInputStyle), { width: '420px', padding: '4px 8px', border: '1px solid #1960a4', borderRadius: '4px', background: '#fff', cursor: 'text' });
var listboxStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 20,
    width: '420px',
    maxWidth: '100%',
    maxHeight: '260px',
    overflowY: 'auto',
    margin: '2px 0 0',
    padding: '4px 0',
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
};
var groupLabelStyle = {
    padding: '4px 10px 2px',
    fontSize: '12px',
    color: '#616161',
};
var separatedGroupStyle = {
    borderTop: '1px solid #ddd',
    marginTop: '4px',
    paddingTop: '2px',
};
var optionStyle = function (active) { return ({
    display: 'flex',
    gap: '8px',
    padding: '4px 10px',
    fontSize: '13px',
    cursor: 'pointer',
    background: active ? '#e6f0fa' : undefined,
}); };
var emptyStyle = __assign(__assign({}, groupLabelStyle), { padding: '4px 10px' });
var SubfieldCombobox = function (_a) {
    var id = _a.id, tag = _a.tag, template = _a.template, catalog = _a.catalog, onChoose = _a.onChoose, placeholder = _a.placeholder, label = _a.label, autoFocus = _a.autoFocus, onDismiss = _a.onDismiss;
    var intl = useIntl();
    var _b = useState(!!autoFocus), open = _b[0], setOpen = _b[1];
    var _c = useState(''), query = _c[0], setQuery = _c[1];
    var _d = useState(0), active = _d[0], setActive = _d[1];
    var items = useMemo(function () { return subfieldSuggestions(template, catalog, query); }, [template, catalog, query]);
    var listId = "".concat(id, "-listbox");
    var optionId = function (s) { return "".concat(id, "-option-").concat(s.code); };
    var current = open ? items[Math.min(active, items.length - 1)] : undefined;
    // The list scrolls (`maxHeight`): keep the option the arrows reached in sight.
    var currentId = current ? optionId(current) : undefined;
    useEffect(function () {
        var _a, _b;
        if (currentId)
            (_b = (_a = document.getElementById(currentId)) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, { block: 'nearest' });
    }, [currentId]);
    var close = function () {
        setOpen(false);
        setQuery('');
        setActive(0);
    };
    var choose = function (s) {
        if (!s)
            return;
        close();
        onChoose(s.code);
    };
    var walk = function (delta) {
        if (!open) {
            setOpen(true);
            return;
        }
        if (items.length === 0)
            return;
        setActive(function (i) { return (Math.min(i, items.length - 1) + delta + items.length) % items.length; });
    };
    var onKeyDown = function (e) {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                walk(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                walk(-1);
                break;
            case 'Enter':
                // Never a submit of the form around it.
                e.preventDefault();
                if (open)
                    choose(current);
                else
                    setOpen(true);
                break;
            case 'Escape':
                // The pane's own Escape (Stripes "close") must not see it.
                e.preventDefault();
                e.stopPropagation();
                close();
                onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss(true);
                break;
            default:
        }
    };
    var renderGroup = function (source, labelId, style) {
        var group = items.filter(function (s) { return s.source === source; });
        if (group.length === 0)
            return null;
        var groupLabel = intl.formatMessage({ id: "ui-czna-authorities.proposalForm.addSubfield.group.".concat(source) });
        return (React.createElement("div", { role: "group", "aria-labelledby": "".concat(id, "-").concat(labelId), style: style },
            React.createElement("div", { id: "".concat(id, "-").concat(labelId), role: "presentation", style: groupLabelStyle }, groupLabel),
            group.map(function (s) { return (
            // Options of a combobox are never focused: the input keeps the focus and points
            // at the active one through `aria-activedescendant` (ARIA combobox pattern).
            // eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events
            React.createElement("div", { key: s.code, id: optionId(s), role: "option", "aria-selected": s === current, "data-add-subfield": s.code, style: optionStyle(s === current), 
                // Keep the focus in the input: the click must not blur (and so close) the list first.
                onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return choose(s); } },
                React.createElement("span", { style: __assign(__assign({}, subfieldCodeStyle), { fontFamily: MONO_FONT }) }, "$".concat(s.code)),
                React.createElement("span", null, s.label))); })));
    };
    var hasTemplate = items.some(function (s) { return s.source === 'template'; });
    return (React.createElement("div", { style: wrapStyle, "data-subfield-add": tag },
        React.createElement("input", { id: id, type: "text", role: "combobox", autoComplete: "off", 
            // eslint-disable-next-line jsx-a11y/no-autofocus -- opened on purpose by "Vložit podpole za…"
            autoFocus: autoFocus, "aria-label": label !== null && label !== void 0 ? label : intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.addSubfield.combobox' }, { tag: tag }), "aria-autocomplete": "list", "aria-expanded": open, "aria-controls": listId, "aria-activedescendant": currentId, placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.addSubfield.placeholder' }), value: query, style: open ? openInputStyle : quietInputStyle, onFocus: function () { return setOpen(true); }, onClick: function () { return setOpen(true); }, onBlur: function () {
                close();
                onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss(false);
            }, onChange: function (e) {
                var next = e.target.value;
                setQuery(next);
                setOpen(true);
                setActive(preferredSuggestion(subfieldSuggestions(template, catalog, next), next));
            }, onKeyDown: onKeyDown }),
        React.createElement("div", { id: listId, role: "listbox", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.addSubfield.menu' }, { tag: tag }), hidden: !open, style: listboxStyle },
            open && renderGroup('template', 'template'),
            open && renderGroup('catalog', 'catalog', hasTemplate ? separatedGroupStyle : undefined),
            open && items.length === 0 && (React.createElement("div", { role: "presentation", style: emptyStyle }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.addSubfield.none' }))))));
};
export default SubfieldCombobox;
